// 全局游戏常量 —— 集中放在这里方便调参
export const GAME_WIDTH = 450;
export const GAME_HEIGHT = 800;

/**
 * 玩法已从"双 lane 桥防"改为"全屏自由战场"：
 * 怪物可以从屏幕顶部任意 x 位置出现，玩家也能自由移动到全屏。
 * 这里保留 LANES 作为"参考网格"，仅用于横幅/Boss 等需要锚点的位置；
 * 不再代表游戏物理上的可走区域。
 */
export const LANES = {
  count: 2,
  /** 仅作为视觉缓冲（贴边时不让小怪卡死出生在屏边），物理上玩家可走全屏 */
  margin: 12,
  get centers(): number[] {
    const usable = GAME_WIDTH - this.margin * 2;
    const laneW = usable / this.count;
    return Array.from({ length: this.count }, (_, i) => this.margin + laneW * (i + 0.5));
  },
};

/** 怪物从屏幕顶部刷出时距左右屏边的最小留白 */
export const SPAWN_MARGIN = 36;

/**
 * 输入分区控制（v4：右下角技能集中区）
 *
 * 战场区：除右下角矩形以外的全部画布（含顶部 HUD 之外的所有空间）
 * 技能区：右下角一块 skillZoneW × skillZoneH 的矩形，A2/ULT/A1 垂直堆叠
 *
 * 拖动会话锁：pointerdown 落在战场区后整个 down→up 都视为移动指令，
 * 即使滑入技能区也继续 follow，不会被打断。
 */
export const INPUT_REGIONS = {
  /** 顶部 HUD 高度（任何 x 的 y < 此值都不响应玩家移动） */
  hudHeight: 90,
  /**
   * 右下角技能区矩形（从屏幕右下角向左上扩展）
   *  - skillZoneW = 80：3 个按钮垂直堆叠所需宽度（按钮 r=28，左右各 12px 内边距）
   *  - skillZoneH = 260：3 个按钮 + 间距，A1 在最下（距底部 64px 让位浏览器工具栏）
   */
  skillZoneW: 80,
  skillZoneH: 260,
} as const;

export const PLAYER = {
  width: 56,
  height: 64,
  color: 0x4cd2ff,
  initialFireDelay: 280,
  /**
   * 玩家移动速度（像素/秒）—— "target follow"模式：
   * 手指点哪儿、玩家就以这个速度走过去，不再瞬移。
   * 太大失去"必须移动"的张力，太小操作起来粘滞。450 ≈ 半屏/秒，
   * 配合 Boss 弹幕速度 310 还有得躲。
   */
  moveSpeed: 450,
  /** 离目标点 ≤ 这个像素就视为到达，停止移动（防抖避免抖动） */
  arriveEps: 2,
} as const;

export const BULLET = {
  /** 圆形碰撞体半径（用于 setCircle）；保持 6 让命中手感不变 */
  radius: 6,
  /** 视觉贴图尺寸（子弹头形：偏窄 + 偏长） */
  textureWidth: 12,
  textureHeight: 22,
  color: 0xffd166,
  speed: -900,
  damage: 5,
} as const;

/**
 * 多重射击编队配置
 *
 * 同时发射 N 颗子弹时的排布规则：
 * - 一行最多 perRow 颗，从左到右等间距，整体相对玩家居中
 * - 超过 perRow 颗就再起一行，新行往"上"叠（更靠近敌人）
 */
export const MULTISHOT = {
  perRow: 5,
  colGap: 14,
  rowGap: 16,
  initial: 1,
  max: 25,
} as const;

// 敌人（小怪）：自由刷新到屏幕顶部任意 x 位置
// 因为可以并排多只，所以尺寸缩小，让画面不至于被堆满
export const ENEMY = {
  width: 64,
  height: 48,
  color: 0xb88c4a,
  borderColor: 0x6a4a22,
  speed: 80,
  baseHp: 60,
  score: 50,
} as const;

export const CHEST = {
  width: 64,
  height: 52,
  color: 0xffd166,
  borderColor: 0x9a6f1d,
  speed: 80,
} as const;

/**
 * 精英怪 —— 比普通怪更稀有，HP/分数/掉落都更高
 * 视觉：紫色 tint + 头顶尖刺光晕
 *
 * 强化要点（再次）：HP×6.5（原 4.8）、出现率 18%（原 14%）、scoreMul 6（原 5）、
 * 体积 1.55（原 1.45）、coinDrop 8（原 5）—— 精英变成"屏幕级威胁"
 */
export const ELITE = {
  /** 出现概率（在非宝箱、非 Boss 的常规刷怪中按此概率替换为精英） */
  spawnRate: 0.18,
  /** 第几档之后才开始出现，避免开局太难 */
  minTier: 2,
  hpMul: 6.5,
  speedMul: 0.85,
  sizeMul: 1.55,
  scoreMul: 6,
  /** 精英死亡时金币掉落（替代普通怪 dropRate 概率掉落，由 MainScene 接入） */
  coinDrop: 8,
  tint: 0xc084ff,
} as const;

/**
 * 射击型精英怪 —— 与普通精英区分的另一类怪种：
 *   - HP / 体型 同普通小怪（不是肉盾型）
 *   - 主动向下发射弹药（复用 BossProjectile，但伤害/外观更弱）
 *   - 视觉：橙红色 tint + 头顶炮口光晕
 *   - 出现时机：tier ≥ minTier 之后才有；与普通精英互斥（先 roll shooter，再 roll elite）
 */
export const ELITE_SHOOTER = {
  /** 出现概率（在非宝箱、非 Boss 的常规刷怪中按此概率替换为射手精英） */
  spawnRate: 0.08,
  minTier: 3,
  /** HP / 速度 / 体型与普通小怪相同（仅在着色 + 行为上区分） */
  hpMul: 1.0,
  speedMul: 0.95,
  sizeMul: 1.0,
  scoreMul: 3,
  /** 死亡固定掉落金币数 */
  coinDrop: 3,
  tint: 0xff8a3c,
  /** 攻击行为参数 */
  attackInterval: 1700,    // 出手间隔（ms）—— 比 Boss 慢得多
  /** 出场后多少 ms 才能开火，避免刚出生在屏外就轰玩家 */
  attackWarmup: 900,
  projectileSpeed: 280,
  projectileDamage: 1,
  /** 子弹颜色（红橙色，区别于 Boss 的纯红） */
  projectileColor: 0xff8a3c,
} as const;

/**
 * 自爆精英 —— 接近玩家就停下倒计时引爆，AOE 圆形伤害
 *
 * 行为流程：
 *   1) 出生后正常下落 + 追击玩家（与普通怪同）
 *   2) 进入 triggerRadius 半径 → 锁定（vy/vx 归 0）+ 头顶亮起红色脉冲
 *   3) fuseMs 后爆炸：以 explodeRadius 圆形 AOE 检测玩家，命中 = 1 点伤害
 *   4) 自身死亡 + 大爆炸特效；护盾/无敌窗口都按普通命中处理
 *
 * 玩家"提前击杀打断引爆"是有意为之的玩法张力：抢在 fuseMs 内打死它即可
 */
export const ELITE_BOMBER = {
  spawnRate: 0.06,
  minTier: 3,
  hpMul: 1.5,
  speedMul: 0.85,
  sizeMul: 1.1,
  scoreMul: 4,
  coinDrop: 4,
  tint: 0xff5566,
  /** 进入此半径锁定 + 倒计时（玩家中心到怪物中心） */
  triggerRadius: 140,
  /** 倒计时时长（引爆前的窗口期） */
  fuseMs: 1200,
  /** 爆炸 AOE 半径（玩家在此范围内吃 1 伤害） */
  explodeRadius: 120,
  /** 爆炸伤害 */
  damage: 1,
} as const;

/**
 * 护盾兵 —— 上半身有能量盾，正面子弹被减伤
 *
 * 行为：
 *   - HP 厚（×3）但移速慢（×0.7）
 *   - takeDamage 时若子弹无 pierce 则伤害 ×shieldDamageMul（0.2 = 仅 20%）
 *   - 子弹有 pierce 直接打满伤害（"穿甲弹无视护盾"），鼓励玩家拿穿透词条
 *   - 视觉：头顶半弧形青色能量盾，被打时盾闪一下
 */
export const ELITE_GUARDIAN = {
  spawnRate: 0.07,
  minTier: 4,
  hpMul: 3.0,
  speedMul: 0.7,
  sizeMul: 1.2,
  scoreMul: 5,
  coinDrop: 6,
  tint: 0x6ad6ff,
  /** 非穿透子弹打盾 = 实际伤害 × 这个倍率 */
  shieldDamageMul: 0.2,
  shieldColor: 0x4cd2ff,
} as const;

/**
 * 普通怪追击玩家配置
 * - initialBiasX：出生时根据玩家相对 x 偏移给一个水平分量（0=纯垂直，1=完全瞄准）
 * - homingTurnRate：每秒最大转向速率（弧度）；玩家移动时怪物会缓慢调头
 * - minVerticalRatio：vy 最小值占 fallSpeed 的比例，防止怪物变成水平横扫
 *   保证最终一定会到屏幕底部
 */
export const ENEMY_CHASE = {
  initialBiasX: 0.55,
  homingTurnRate: 0.9,
  minVerticalRatio: 0.5,
} as const;

/** 难度阶梯（tier-based 渐进难度） */
export const DIFFICULTY = {
  tierDuration: 16400,     // 每阶持续略缩短（原 18000，整体节奏 +10%）
  hpMul: 1.4,
  speedMul: 1.1,
  intervalMul: 0.93,       // 刷怪间隔缩短更快（原 0.935）
  initialInterval: 1620,   // 开局更稀（原 1380）—— 给玩家适应时间
  minInterval: 440,        // 间隔下限再拉低（原 510）
  /**
   * 每次刷新时的随机抖动比例 [0, 1]：
   *   实际间隔 = base × random(1 - jitter, 1 + jitter)
   */
  intervalJitter: 0.4,
  baseSpeed: 70,           // 开局速度更慢（原 82）
  maxSpeed: 220,
  initialChestRate: 0.20,
  chestRateDecay: 0.010,
  minChestRate: 0.06,

  giftChestsPerTier: 2,
  giftChestStagger: 350,

  bossEveryNTiers: 5,
  bossHpMul: 45,           // Boss HP 减半（原 90）—— 缩短持久战
  bossSpeedMul: 0.6,      // Boss 移速再提（原 0.6）
  bossDropChests: 3,

  /**
   * 前几波"暖场"折扣 —— 让新玩家有时间熟悉操作
   *
   * 影响（仅 tier < rampTiers 时生效）：
   *   - enemyHp ×= rampHpMul[tier]    （越前面越软）
   *   - fallSpeed ×= rampSpeedMul[tier]
   *   - spawnInterval ×= rampIntervalMul[tier]（>1 = 更稀）
   *   - tierDuration ×= rampTierDurMul[tier]（前几波更长，给玩家喘息）
   *
   * 数组从 tier 0 开始，索引到 rampTiers 时回归 1.0
   */
  rampTiers: 3,
  rampHpMul:        [0.55, 0.75, 0.9],
  rampSpeedMul:     [0.78, 0.88, 0.95],
  rampIntervalMul:  [1.5,  1.25, 1.1],
  rampTierDurMul:   [1.4,  1.2,  1.05],
} as const;

// Boss 基础参数
export const BOSS = {
  width: 240,
  height: 110,
  color: 0x7a3030,
  borderColor: 0x2a0a0a,
  scoreReward: 900,        // 分数奖励再提（原 700）

  /**
   * Boss 悬停 y 坐标比例（到达屏幕高度 × 此比例时停下，原地打弹幕 + zigzag）
   * 0.22 ≈ 屏幕上方 1/4 处，玩家有充足下方空间躲避
   */
  hoverYRatio: 0.22,
  /** 接近 hoverY 时开始减速的距离（像素），太小会硬刹车，太大刚出场就慢下来 */
  hoverBrakeRange: 80,
  /**
   * 悬停巡逻：standard / splitter 这种 zigzagAmp=0 的 Boss，到位后按这套参数横向缓慢摆动
   * （zigzag 类型已经有自己的更大摆动，不会叠加）
   */
  hoverPatrolAmp: 90,
  hoverPatrolFreq: 1.4,

  attackInterval: 1150,    // Boss 出手更密（原 1400）
  projectileSpeed: 230,    // 弹速再降（原 280）—— 用户反馈太快躲不掉
  projectileRadius: 14,
  projectileColor: 0xff5566,
  projectileDamage: 1,

  /**
   * 扇形多发：每次攻击发射 salvoCount 颗弹，左右总跨度 salvoSpread 弧度
   * 1 颗 = 老的"单发跟踪"行为；3 颗 = 中间一颗 + 左右各一颗扇形
   */
  salvoCount: 6,           // 扇形弹数 5→6
  salvoSpread: 0.85,       // 总跨度 0.7→0.85（≈ 49°）

  /**
   * Boss 子弹追击参数
   * - homingTurnRate: 每秒最大转向速率（弧度），值越大越"灵敏"，玩家越难躲
   * - homingMaxTime: 追击多少秒后停止（保证最终能甩开），ms
   *   过了这段时间子弹会保持当前速度直线飞，避免"屎一样追到底"
   */
  projectileHomingTurnRate: 2.2,   // 追踪再灵敏（原 1.9）
  projectileHomingMaxTime: 1600,

  /**
   * Boss 二阶段（狂暴）阈值与加成
   *   - phase2HpRatio：HP 降到 maxHp × 此比例时进入二阶段（仅触发一次）
   *   - phase2AttackIntervalMul：二阶段攻击间隔倍率（< 1 = 更密）
   *   - phase2SalvoBonus：二阶段额外多发的扇形子弹数
   *   - phase2SpeedMul：二阶段下落 / 横移速度倍率
   *   - phase2SpreadAdd：扇形角度增量（弧度）
   *   - phase2ProjectileSpeedMul：二阶段弹速倍率（< 1 = 更慢，给玩家躲弹幕窗口）
   */
  phase2HpRatio: 0.5,
  phase2AttackIntervalMul: 0.55,
  phase2SalvoBonus: 2,
  phase2SpeedMul: 1.35,
  phase2SpreadAdd: 0.35,
  /**
   * 狂暴时弹速反而要降：二阶段弹更密 + 扇形更宽，如果再加速会变成"看不到就死"，
   * 所以这里 0.9× 让玩家依然能用走位+闪现躲掉
   */
  phase2ProjectileSpeedMul: 0.9,
} as const;

/**
 * 战场天气：每次升档（_onTierUp）按 spawnRate 概率刷一种 modifier，
 * 持续到下一波（即 tierDuration ms）。最多同时一个，不叠加。
 *
 * none = 没天气（占主要权重，避免每波都过载）
 * 其余各类型见 WeatherKind 注释。
 */
export type WeatherKind = 'none' | 'wind' | 'magnet-fail' | 'meteor' | 'goldRush';

export interface WeatherConfig {
  label: string;
  /** 短描述（横幅副标题） */
  desc: string;
  /** 该天气的颜色主调（横幅 / 屏角 vignette） */
  tint: number;
  /** roll 权重（none 权重最大，其它越大越常见） */
  weight: number;
}

export const WEATHER: Record<WeatherKind, WeatherConfig> = {
  'none':         { label: '无',   desc: '',                       tint: 0xffffff, weight: 50 },
  'wind':         { label: '狂风', desc: '所有弹药横向受风',         tint: 0xb8e0ff, weight: 14 },
  'magnet-fail':  { label: '磁暴', desc: '磁吸失效 · 射速 +15%',     tint: 0x9b59ff, weight: 12 },
  'meteor':       { label: '陨石', desc: '屏侧间歇陨石坠落',         tint: 0xff6644, weight: 14 },
  'goldRush':     { label: '财源', desc: '金币 ×2 · 刷怪加密',       tint: 0xffd166, weight: 10 },
};

/** 每个天气的具体参数（与上表分离，便于细调） */
export const WEATHER_FX = {
  /** 狂风：风向每 N ms 翻一次（左/右随机），强度 px/s */
  wind: { switchMs: 4500, strength: 110 },
  /** 磁暴：玩家射速间隔倍率（< 1 = 更快） */
  magnetFail: { fireRateMul: 0.85 },
  /** 陨石：每多少 ms 落一颗 */
  meteor: { intervalMs: 2400, damage: 1, radius: 80, fallSpeed: 360 },
  /** 财源：金币 ×N + spawnInterval × M */
  goldRush: { coinMul: 2, spawnIntervalMul: 0.7 },
} as const;

/**
 * 元素子弹 buff —— 现在由独立的彩色 buff 箱子（fire/ice/chain）触发，
 * 玩家撞到立即获得 8s 限时弹药效果。
 * 老的"金宝箱有概率变 buff"（chestProcRate）已废弃 —— 改成 buff 箱独立刷怪。
 */
export type BulletBuffKind = 'fire' | 'ice' | 'chain';

export interface BulletBuffConfig {
  label: string;
  desc: string;
  tint: number;
  /** 持续时间（ms） */
  durationMs: number;
}

export const BULLET_BUFF: Record<BulletBuffKind, BulletBuffConfig> = {
  fire:  { label: '火焰弹', desc: '命中后留下灼烧地圈',       tint: 0xff6a3c, durationMs: 8000 },
  ice:   { label: '冰冻弹', desc: '命中后该敌减速 60% 持续 2s', tint: 0x6ad6ff, durationMs: 8000 },
  chain: { label: '闪电链', desc: '命中后向最近 2 敌连锁',     tint: 0xc0a8ff, durationMs: 8000 },
};

export const BULLET_BUFF_FX = {
  /**
   * Buff 箱子的刷怪概率：在常规刷怪 roll 时，先 roll 普通宝箱，
   * 没中再 roll buff 箱（独立概率，不与普通宝箱叠加）。
   * 0.04 ≈ 每 25 个怪刷一个 buff 箱（比之前 18% 触发的拾取频率更低，但效果保留 8s 更值得）
   */
  spawnRate: 0.04,
  /** Buff 箱要求的最低难度档（避免开局就出，让玩家先熟悉基础玩法） */
  minTier: 2,
  /** 火焰：地圈伤害（每 tick）+ tick 间隔 + 圈半径 */
  fire: { tickDamage: 3, tickIntervalMs: 250, radius: 70, lifetimeMs: 1500 },
  /** 冰：减速倍率 + 持续时长 */
  ice: { slowMul: 0.4, slowMs: 2000 },
  /** 闪电：连锁数量 + 衰减 */
  chain: { jumps: 2, dmgFalloff: 0.5, range: 220 },
} as const;

/**
 * Combo 连杀：1.5s 内连续击杀逐渐升 combo，断了清零
 *   - rewardEveryN：每达成 N 段奖励 1 金币
 *   - breakBonusMul：断 combo 时奖励额外分数 = comboMax × 此倍率
 *   - bigComboThreshold：达到此 combo 才会触发"怒火"传说词条的伤害加成（如果玩家拿了那张卡）
 */
export const COMBO = {
  windowMs: 1500,
  rewardEveryN: 5,
  breakBonusMul: 5,
  bigComboThreshold: 10,
  bigComboDamageMul: 1.3,
} as const;


export interface BossTypeConfig {
  label: string;
  hpMul: number;
  sizeMul: number;
  tint: number;
  zigzagAmp: number;
  zigzagFreq: number;
  splitOnDeath: number;
  splitMiniHpRatio?: number;
  splitMiniSizeMul?: number;
}

/** Boss 种类 —— 见 Spawner._pickBossType */
export type BossTypeKey = 'standard' | 'zigzag' | 'splitter';

export const BOSS_TYPES: Record<BossTypeKey, BossTypeConfig> = {
  standard: {
    label: '皇冠胖王',
    hpMul: 1.0,
    sizeMul: 1.0,
    tint: 0xffffff,
    zigzagAmp: 0,
    zigzagFreq: 0,
    splitOnDeath: 0,
  },
  zigzag: {
    label: '蛇行胖王',
    hpMul: 1.0,              // HP 略提（原 0.95）
    sizeMul: 0.95,
    tint: 0x66ccff,
    zigzagAmp: 130,          // 摆幅再加大（原 110）
    zigzagFreq: 3.2,         // 频率再加快（原 2.8）
    splitOnDeath: 0,
  },
  splitter: {
    label: '分裂胖王',
    hpMul: 1.4,              // HP 再提（原 1.3）
    sizeMul: 1.0,
    tint: 0xc084ff,
    zigzagAmp: 0,
    zigzagFreq: 0,
    splitOnDeath: 4,         // 分裂数量 3 → 4
    splitMiniHpRatio: 0.45,  // 分裂体血更厚（原 0.4）
    splitMiniSizeMul: 0.55,
  },
};

/** 金币 */
export const COIN = {
  radius: 9,
  color: 0xffe066,
  borderColor: 0xa07a10,
  speed: 60,
  dropRate: 0.7,
  dropAmount: 1,
  bossDropAmount: 8,

  /**
   * 磁吸：默认全屏吸附（设成对角线长度），玩家不用走过去也能拿到
   * sqrt(450² + 800²) ≈ 918，给点余量用 1000
   */
  magnetRadius: 1000,
  magnetAccel: 1400,
  magnetMaxSpeed: 800,
} as const;

/** 商店 */
export const SHOP = {
  rerollCost: 5,
  rerollCostInc: 3,
  healCost: 8,
  healAmount: 2,
  /** 祝福抽：保证抽到至少 1 张稀有以上的卡（其余 2 张正常 roll） */
  blessingCost: 15,
  /** 祝福抽花费递增（每用一次 +N，不让无脑刷） */
  blessingCostInc: 8,
} as const;

/** 子弹高级行为 */
export const BULLET_FX = {
  homingRange: 220,
  homingTurnRate: 6.5,
} as const;

/** 玩家受击保护 + 复活 */
export const PLAYER_FX = {
  invincibleMs: 1000,         // 无敌窗口加长（原 800）
  reviveCoinCost: 20,         // 复活更便宜（原 25）
} as const;

/**
 * 主动技能：短闪（dash）—— 第 1 槽（A1）的默认技能；A1 也可以换成其它主动技能
 *
 * 玩家点击 HUD 右下角技能按钮（或按空格 / Z 键）：
 *   - 朝当前移动目标方向闪现 distance 像素（无目标 = 朝屏幕中央）
 *   - 闪现期间 invincibleMs 内无敌（吃 takeDamage 直接 blocked）
 *   - 触发后进入 cooldownMs 冷却，HUD 按钮转灰 + 显示倒计时
 *   - 出生即可用一次（不上来就锁）
 *
 * 强度调参：distance 太大会破坏全屏自由移动的张力，180-220 这个量级
 * 大致等于"穿过半个手机屏幕"，配合 0.5s 无敌足够躲一波弹幕。
 */
export const DASH = {
  distance: 200,
  invincibleMs: 500,
  cooldownMs: 8000,
} as const;

/** Web Audio 合成音效 */
export const SOUND = {
  enabled: true,
  masterVolume: 0.18,
} as const;

/** 难度预设：MenuScene 写入 registry，Difficulty 读取后倍率叠加 */
export type DifficultyPresetKey = 'easy' | 'normal' | 'hard';

export interface DifficultyPreset {
  label: string;
  hpMul: number;
  intervalMul: number;
  speedMul: number;
  tierDurMul: number;
}

export const DIFFICULTY_PRESETS: Record<DifficultyPresetKey, DifficultyPreset> = {
  easy:   { label: '休闲', hpMul: 0.6, intervalMul: 1.3, speedMul: 0.8,  tierDurMul: 1.5 },
  normal: { label: '普通', hpMul: 0.85, intervalMul: 1.1, speedMul: 0.92, tierDurMul: 1.15 },
  hard:   { label: '困难', hpMul: 1.25, intervalMul: 0.9, speedMul: 1.08, tierDurMul: 0.85 },
};

/**
 * 游戏模式：
 *   - campaign：撑过 totalWaves 波（最后一波为 Final Boss）即通关，弹胜利面板
 *   - endless：现有玩法不变，难度按 tier 无限上升，死亡即结束
 *
 * 两个模式的最高分独立存储（highScore.ts 用 mode 作为 key 后缀）。
 */
export type GameModeKey = 'campaign' | 'endless';

export interface GameModeConfig {
  label: string;
  desc: string;
  /** Infinity = 无尽 */
  totalWaves: number;
  /** Final Boss HP 倍率（仅 campaign，最后一波那只 Boss 比普通 Boss 强多少） */
  finalBossHpMul: number;
}

export const GAME_MODES: Record<GameModeKey, GameModeConfig> = {
  campaign: {
    label: '关卡模式',
    desc: '撑过 15 波即可通关',
    totalWaves: 15,
    finalBossHpMul: 1.8,
  },
  endless: {
    label: '无尽模式',
    desc: '难度无限上升 · 比拼最高分',
    totalWaves: Infinity,
    finalBossHpMul: 1,
  },
};

// ═══════════════════════════════════════════════════════════════
// 技能系统：主动 1（短 CD，灵活）+ 主动 2（中 CD，威力中）+ ULT（蓄能放，大动作）
// ═══════════════════════════════════════════════════════════════

/** 主动技能 key —— 玩家在 MenuScene 里从这些里选 1 个放在 A1 + 1 个放在 A2 */
export type ActiveSkillKey = 'dash' | 'pulse' | 'timewarp';
/** ULT key —— 放在 U 槽 */
export type UltSkillKey = 'nuke' | 'frenzy';

export interface ActiveSkillConfig {
  key: ActiveSkillKey;
  label: string;
  icon: string;          // emoji / 单字符
  shortDesc: string;     // 一行简介（菜单选择 + HUD 长按）
  cooldownMs: number;    // 冷却时长
  /** HUD 按钮的强调色（hex 数字） */
  tint: number;
}

export interface UltSkillConfig {
  key: UltSkillKey;
  label: string;
  icon: string;
  shortDesc: string;
  /** 蓄能上限 —— 玩家累积到 chargeMax 才能放 */
  chargeMax: number;
  /**
   * 蓄能源描述（仅展示用；真正的蓄能写入由 SkillSystem 的 hooks 注入）
   * 如 '击杀 +1' 'Combo +1段 +1' 等
   */
  chargeSource: string;
  tint: number;
}

export const ACTIVE_SKILLS: Record<ActiveSkillKey, ActiveSkillConfig> = {
  dash: {
    key: 'dash',
    label: '短闪',
    icon: '⚡',
    shortDesc: '朝目标方向闪现 + 0.5s 无敌',
    cooldownMs: 8000,
    tint: 0x4cd2ff,
  },
  pulse: {
    key: 'pulse',
    label: '震波',
    icon: '💥',
    shortDesc: '玩家位置 180 半径炸开 · 推开+伤害',
    cooldownMs: 10000,
    tint: 0xffd166,
  },
  timewarp: {
    key: 'timewarp',
    label: '时缓',
    icon: '⏳',
    shortDesc: '全场敌人/弹幕速度 ×0.35 持续 3s',
    cooldownMs: 16000,
    tint: 0xc084ff,
  },
};

export const ULT_SKILLS: Record<UltSkillKey, UltSkillConfig> = {
  nuke: {
    key: 'nuke',
    label: '核爆',
    icon: '💣',
    shortDesc: '清屏所有非 Boss 怪 + Boss 受重伤',
    chargeMax: 80,
    chargeSource: '击杀 +1',
    tint: 0xff5566,
  },
  frenzy: {
    key: 'frenzy',
    label: '狂射',
    icon: '🔫',
    shortDesc: '5s 内射速 ×2.5 · 弹数 +5 · 必暴',
    chargeMax: 100,
    chargeSource: '击杀 +1 · 受击 +5',
    tint: 0xff8a3c,
  },
};

/** 技能调参（具体数值，独立于 ACTIVE_SKILLS 的元信息） */
export const SKILL_FX = {
  pulse: {
    radius: 180,
    /** 推开力（向外的速度脉冲，px/s） */
    knockback: 520,
    /** 伤害（与玩家 bulletDamage 无关 —— 技能就是固定值，不被堆叠左右） */
    damage: 40,
    /** 玩家自身震波期间的无敌（起手到推开完成） */
    invincibleMs: 350,
  },
  timewarp: {
    /** physics.world.timeScale 设成多少（>1 = 慢；2.85 ≈ 1/0.35 倍慢） */
    worldTimeScale: 2.85,
    durationMs: 3000,
    /** 玩家自身在效果期间不受减速（玩家移动 / 射击照常）—— 通过单独的 fireRateMul 和 stepMove 不被 timeScale 影响实现 */
  },
  nuke: {
    /** Boss 直接扣的 HP 比例（核爆 = 大招但不该秒 Boss） */
    bossHpRatio: 0.18,
  },
  frenzy: {
    durationMs: 5000,
    fireRateMul: 0.4,    // 射速间隔 ×0.4 = 射速 ×2.5
    bulletCountAdd: 5,
    /** 期间 critChance 强制设为 1（必暴） */
    forceCrit: true,
  },
} as const;

